/**
 * Display Engine — Template Factory
 * Maps template_id from theme to the right React component.
 * Each template is industry-agnostic and reads from the dynamic field schema.
 */
import React, { useEffect, useState } from 'react';

// Utility: get a typed value out of an item's data bag with formatting
function formatValue(value, fieldType) {
  if (value === null || value === undefined || value === '') return '';
  switch (fieldType) {
    case 'currency':
      return '₹' + Number(value).toLocaleString('en-IN');
    case 'number':
      return Number(value).toLocaleString('en-IN');
    case 'boolean':
      return value ? '✓' : '✗';
    default:
      return String(value);
  }
}

function fieldByKey(fields, key) {
  return fields.find(f => f.key === key);
}

// =================== GRID VIEW ===================
function GridTemplate({ client, theme, fields, items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 md:p-8" data-testid="tpl-grid">
      {items.map(item => (
        <div key={item.id} className="rounded-2xl overflow-hidden shadow-xl border" style={{ backgroundColor: theme.secondary_color + 'cc', borderColor: theme.primary_color + '40' }}>
          {item.image_urls?.[0] && (
            <div className="aspect-square w-full overflow-hidden bg-black/30">
              <img src={item.image_urls[0]} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition duration-500" />
            </div>
          )}
          <div className="p-3 md:p-4">
            <h3 className="font-bold text-base md:text-lg mb-1 line-clamp-2" style={{ color: theme.text_color, fontFamily: theme.heading_font || theme.font_family }}>{item.title}</h3>
            {item.category && <p className="text-xs mb-2 opacity-70" style={{ color: theme.accent_color }}>{item.category}</p>}
            <div className="space-y-1 text-sm" style={{ color: theme.text_color }}>
              {fields.slice(0, 4).map(f => {
                const v = item.data?.[f.key];
                if (v === undefined || v === '') return null;
                const isPrice = f.field_type === 'currency';
                return (
                  <div key={f.key} className={`flex justify-between gap-2 ${isPrice ? 'font-bold text-base' : 'text-xs opacity-80'}`}>
                    <span className="opacity-70">{f.label}</span>
                    <span style={isPrice ? { color: theme.accent_color } : {}}>{formatValue(v, f.field_type)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =================== PROMO BANNER (auto-rotate hero) ===================
function PromoBannerTemplate({ client, theme, fields, items }) {
  const [idx, setIdx] = useState(0);
  const interval = (theme.rotation_seconds || 8) * 1000;
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  const item = items[idx];
  if (!item) return <div className="p-8 text-center opacity-60">No items</div>;
  return (
    <div className="relative w-full h-full min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="tpl-promo">
      {item.image_urls?.[0] && (
        <img src={item.image_urls[0]} alt={item.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />
      )}
      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${theme.background_color}ee 0%, ${theme.secondary_color}aa 100%)` }} />
      <div className="relative z-10 max-w-4xl text-center p-8">
        <p className="uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-3" style={{ color: theme.accent_color }}>{item.category || client.name}</p>
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 leading-tight" style={{ color: theme.text_color, fontFamily: theme.heading_font || theme.font_family }}>{item.title}</h1>
        <div className="flex flex-wrap gap-x-8 gap-y-3 justify-center text-base md:text-xl" style={{ color: theme.text_color }}>
          {fields.slice(0, 3).map(f => {
            const v = item.data?.[f.key];
            if (v === undefined || v === '') return null;
            const isPrice = f.field_type === 'currency';
            return (
              <div key={f.key} className="flex flex-col items-center">
                <span className="text-xs uppercase tracking-wider opacity-60">{f.label}</span>
                <span className={isPrice ? 'text-3xl md:text-5xl font-extrabold' : 'font-semibold'} style={isPrice ? { color: theme.accent_color } : {}}>
                  {formatValue(v, f.field_type)}
                </span>
              </div>
            );
          })}
        </div>
        {items.length > 1 && (
          <div className="flex gap-2 justify-center mt-8">
            {items.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition ${i === idx ? 'w-8' : 'opacity-40'}`} style={{ backgroundColor: i === idx ? theme.accent_color : theme.text_color }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =================== MENU LIST ===================
function MenuListTemplate({ client, theme, fields, items }) {
  // Group items by `category` if present, else flat
  const groups = items.reduce((acc, it) => {
    const g = it.category || 'All';
    (acc[g] = acc[g] || []).push(it);
    return acc;
  }, {});
  const priceField = fields.find(f => f.field_type === 'currency') || fieldByKey(fields, 'price_inr');
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-10" data-testid="tpl-menu">
      {Object.keys(groups).map(group => (
        <section key={group} className="mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 pb-2 border-b-2"
              style={{ color: theme.accent_color, borderColor: theme.primary_color + '60', fontFamily: theme.heading_font || theme.font_family }}>
            {group}
          </h2>
          <div className="space-y-3">
            {groups[group].map(item => (
              <div key={item.id} className="flex items-start justify-between gap-4 py-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl md:text-2xl font-bold" style={{ color: theme.text_color }}>{item.title}</h3>
                    {item.data?.veg !== undefined && (
                      <span className={`inline-block w-3 h-3 border-2 ${item.data.veg ? 'border-green-500' : 'border-red-500'}`}>
                        <span className={`block w-1.5 h-1.5 m-0.5 rounded-full ${item.data.veg ? 'bg-green-500' : 'bg-red-500'}`} />
                      </span>
                    )}
                  </div>
                  {item.data?.description && (
                    <p className="text-sm md:text-base opacity-70 mt-1" style={{ color: theme.text_color }}>{item.data.description}</p>
                  )}
                  <div className="flex gap-3 mt-2 text-xs opacity-60">
                    {fields.filter(f => !['price_inr', 'description', 'section'].includes(f.key)).slice(0, 3).map(f => {
                      const v = item.data?.[f.key];
                      if (v === undefined || v === '' || v === null) return null;
                      return <span key={f.key} style={{ color: theme.text_color }}>· {f.label}: {formatValue(v, f.field_type)}</span>;
                    })}
                  </div>
                </div>
                {priceField && item.data?.[priceField.key] !== undefined && (
                  <div className="text-2xl md:text-3xl font-extrabold whitespace-nowrap"
                       style={{ color: theme.accent_color, fontFamily: theme.heading_font || theme.font_family }}>
                    {formatValue(item.data[priceField.key], 'currency')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// =================== MINIMALIST CARD (single rotating big card) ===================
function MinimalistCardTemplate({ client, theme, fields, items }) {
  const [idx, setIdx] = useState(0);
  const interval = (theme.rotation_seconds || 10) * 1000;
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  const item = items[idx];
  if (!item) return <div className="p-8 text-center opacity-60">No items</div>;
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4 md:p-12" data-testid="tpl-minimalist">
      <div className="grid md:grid-cols-2 gap-8 md:gap-16 max-w-6xl w-full items-center">
        {item.image_urls?.[0] ? (
          <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl" style={{ border: `1px solid ${theme.primary_color}40` }}>
            <img src={item.image_urls[0]} alt={item.title} className="w-full h-full object-cover" />
          </div>
        ) : <div />}
        <div>
          {item.category && (
            <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-3" style={{ color: theme.accent_color }}>{item.category}</p>
          )}
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight" style={{ color: theme.text_color, fontFamily: theme.heading_font || theme.font_family }}>{item.title}</h1>
          <div className="space-y-3 mb-6">
            {fields.slice(0, 5).map(f => {
              const v = item.data?.[f.key];
              if (v === undefined || v === '' || v === null) return null;
              const isPrice = f.field_type === 'currency';
              return (
                <div key={f.key} className="flex items-baseline justify-between border-b pb-2" style={{ borderColor: theme.primary_color + '30' }}>
                  <span className="text-sm uppercase tracking-wider opacity-60" style={{ color: theme.text_color }}>{f.label}</span>
                  <span className={isPrice ? 'text-3xl font-extrabold' : 'text-base font-semibold'} style={isPrice ? { color: theme.accent_color } : { color: theme.text_color }}>
                    {formatValue(v, f.field_type)}
                  </span>
                </div>
              );
            })}
          </div>
          {items.length > 1 && (
            <div className="flex gap-2">
              {items.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`h-1.5 transition-all ${i === idx ? 'w-10' : 'w-4 opacity-40'}`} style={{ backgroundColor: i === idx ? theme.accent_color : theme.text_color }} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =================== SPLIT SCREEN (hero left + scrolling grid right) ===================
function SplitScreenTemplate({ client, theme, fields, items }) {
  const [heroIdx, setHeroIdx] = useState(0);
  const interval = (theme.rotation_seconds || 8) * 1000;
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  const hero = items[heroIdx];
  const grid = items.filter((_, i) => i !== heroIdx).slice(0, 9);
  return (
    <div className="grid md:grid-cols-2 min-h-[70vh]" data-testid="tpl-split">
      {/* Hero side */}
      <div className="relative overflow-hidden flex items-center justify-center p-6" style={{ backgroundColor: theme.secondary_color + 'cc' }}>
        {hero?.image_urls?.[0] && (
          <img src={hero.image_urls[0]} alt={hero.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="relative z-10 text-center max-w-xl">
          {hero?.category && <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-2" style={{ color: theme.accent_color }}>{hero.category}</p>}
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 leading-tight" style={{ color: theme.text_color, fontFamily: theme.heading_font || theme.font_family }}>{hero?.title}</h1>
          {hero && fields.filter(f => f.field_type === 'currency').slice(0, 1).map(f => (
            <div key={f.key} className="text-4xl md:text-6xl font-extrabold mt-4" style={{ color: theme.accent_color }}>
              {formatValue(hero.data?.[f.key], f.field_type)}
            </div>
          ))}
        </div>
      </div>
      {/* Grid side */}
      <div className="p-4 md:p-6 grid grid-cols-2 md:grid-cols-3 gap-3 content-start">
        {grid.map(item => (
          <div key={item.id} className="rounded-xl overflow-hidden border" style={{ backgroundColor: theme.secondary_color + '88', borderColor: theme.primary_color + '30' }}>
            {item.image_urls?.[0] && <img src={item.image_urls[0]} alt={item.title} className="w-full h-24 object-cover" />}
            <div className="p-2">
              <div className="font-bold text-sm truncate" style={{ color: theme.text_color }}>{item.title}</div>
              {fields.find(f => f.field_type === 'currency') && (
                <div className="text-sm font-bold mt-1" style={{ color: theme.accent_color }}>
                  {formatValue(item.data?.[fields.find(f => f.field_type === 'currency').key], 'currency')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =================== WEATHER WIDGET (grid + live weather card) ===================
function WeatherWidgetTemplate({ client, theme, fields, items }) {
  const [weather, setWeather] = useState(null);
  const city = theme.weather_city || client.contact_phone /* placeholder */ || 'New Delhi';
  const unit = (theme.weather_unit || 'C').toUpperCase();
  useEffect(() => {
    const apiRoot = process.env.REACT_APP_BACKEND_URL || '';
    const load = () => {
      fetch(`${apiRoot}/api/displayengine/weather?city=${encodeURIComponent(city)}&unit=${unit}`)
        .then(r => r.ok ? r.json() : null).then(setWeather).catch(() => setWeather(null));
    };
    load();
    const id = setInterval(load, 10 * 60 * 1000); // refresh every 10 min
    return () => clearInterval(id);
  }, [city, unit]);
  return (
    <div className="relative" data-testid="tpl-weather">
      {weather && (
        <div className="absolute top-4 right-4 z-10 rounded-2xl p-4 backdrop-blur shadow-xl border min-w-[200px]"
             style={{ backgroundColor: theme.secondary_color + 'dd', borderColor: theme.primary_color + '40' }} data-testid="weather-card">
          <div className="text-3xl mb-1">{weather.icon}</div>
          <div className="text-xs uppercase opacity-70" style={{ color: theme.text_color }}>{weather.city}</div>
          <div className="text-4xl font-extrabold" style={{ color: theme.accent_color, fontFamily: theme.heading_font || theme.font_family }}>
            {unit === 'F' ? `${weather.temp_f}°F` : `${weather.temp_c}°C`}
          </div>
          <div className="text-xs opacity-80" style={{ color: theme.text_color }}>{weather.condition}</div>
          <div className="text-[10px] opacity-50 mt-1" style={{ color: theme.text_color }}>
            💧 {weather.humidity}% · 💨 {weather.wind_kmh} km/h
          </div>
        </div>
      )}
      {/* Render the standard grid below */}
      <GridTemplate client={client} theme={theme} fields={fields} items={items} />
    </div>
  );
}

// =================== GOLD RATE BOARD (LED-style jewellery price display) ===================
function GoldRateBoardTemplate({ client, theme, fields, items }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const priceField = fields.find(f => f.field_type === 'currency') || fieldByKey(fields, 'price_inr') || fieldByKey(fields, 'rate');
  const dateStr = now.toLocaleDateString('en-GB').replace(/\//g, '.'); // DD.MM.YY style
  const timeStr = now.toLocaleTimeString('en-GB', { hour12: false });
  const ledFont = '"DSEG7Classic", "Orbitron", "VT323", monospace';

  return (
    <div
      className="relative w-full min-h-[80vh] flex items-center justify-center p-4 md:p-10"
      style={{
        backgroundImage: 'radial-gradient(ellipse at top, #2a1a08 0%, #0a0604 60%, #000 100%)',
      }}
      data-testid="tpl-gold-rate"
    >
      {/* Decorative shop banner */}
      <div className="absolute top-2 md:top-4 left-1/2 -translate-x-1/2 flex items-center gap-3 md:gap-6 px-4 md:px-8 py-2 md:py-3 rounded-lg shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #3a2410, #1a0f06)',
          borderTop: `3px solid ${theme.accent_color || '#d4a648'}`,
          borderBottom: `3px solid ${theme.accent_color || '#d4a648'}`,
        }}
      >
        {theme.show_logo && theme.logo_url && (
          <img src={theme.logo_url} alt={client.name} className="h-10 md:h-14 object-contain" />
        )}
        <div className="text-center">
          <div
            className="text-xl md:text-3xl font-extrabold tracking-wide"
            style={{ color: theme.accent_color || '#d4a648', fontFamily: theme.heading_font || 'Georgia, serif' }}
          >
            {client.name}
          </div>
          {client.description && (
            <div className="text-[10px] md:text-xs italic" style={{ color: '#c2a16a' }}>{client.description}</div>
          )}
        </div>
      </div>

      {/* Main Board */}
      <div
        className="mt-20 md:mt-28 w-full max-w-3xl rounded-xl p-4 md:p-8 shadow-2xl border-8"
        style={{
          background: 'linear-gradient(160deg, #1a1209 0%, #0c0703 100%)',
          borderColor: '#a87a2a',
          borderImage: 'linear-gradient(135deg, #d4a648 0%, #8a5a1a 50%, #d4a648 100%) 1',
        }}
      >
        {/* Board Title */}
        <div
          className="text-center text-2xl md:text-5xl font-extrabold mb-4 md:mb-6 tracking-[0.15em]"
          style={{
            color: theme.accent_color || '#d4a648',
            fontFamily: theme.heading_font || 'Georgia, serif',
            textShadow: '0 0 16px rgba(212,166,72,0.4)',
          }}
        >
          {theme.board_title || 'GOLD RATE'}
        </div>

        {/* Date / Time LED Row */}
        <div className="grid grid-cols-2 gap-2 md:gap-4 mb-4 md:mb-6">
          <LedTile label="DATE" value={dateStr} ledFont={ledFont} />
          <LedTile label="TIME" value={timeStr} ledFont={ledFont} />
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-2 gap-2 md:gap-4 mb-2 md:mb-3 text-center text-xs md:text-base font-bold uppercase tracking-widest"
          style={{ color: '#e8d8b0' }}>
          <div className="py-1.5 md:py-2 border-2 rounded" style={{ borderColor: '#a87a2a55' }}>ITEM</div>
          <div className="py-1.5 md:py-2 border-2 rounded" style={{ borderColor: '#a87a2a55' }}>
            {theme.rate_label || "Today's RATE 10 Per gram"}
          </div>
        </div>

        {/* Item rows */}
        <div className="space-y-2 md:space-y-3">
          {(items.length ? items : [
            { id: 'p1', title: '24 CT', data: { [priceField?.key || 'price_inr']: '0' } },
            { id: 'p2', title: '22 CT', data: { [priceField?.key || 'price_inr']: '0' } },
            { id: 'p3', title: '18 CT', data: { [priceField?.key || 'price_inr']: '0' } },
            { id: 'p4', title: 'SILVER', data: { [priceField?.key || 'price_inr']: '0' } },
          ]).slice(0, 6).map(it => {
            const raw = priceField ? it.data?.[priceField.key] : null;
            const num = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : 0;
            return (
              <div key={it.id} className="grid grid-cols-2 gap-2 md:gap-4 items-center">
                <div
                  className="py-2 md:py-3 px-3 md:px-4 text-xl md:text-4xl font-extrabold tracking-wider"
                  style={{
                    color: '#f0deae',
                    fontFamily: theme.heading_font || 'Georgia, serif',
                    textShadow: '0 1px 0 rgba(0,0,0,0.6)',
                  }}
                >
                  {it.title}
                </div>
                <LedTile value={String(Math.round(num)).padStart(6, '0')} ledFont={ledFont} big />
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-4 md:mt-6 text-center text-[10px] md:text-base font-extrabold tracking-[0.25em]"
          style={{ color: '#e8d8b0', textShadow: '0 0 8px rgba(212,166,72,0.25)' }}>
          {theme.footer_text || 'MAKING & GST CHARGES EXTRA'}
        </div>
      </div>
    </div>
  );
}

function LedTile({ label, value, ledFont, big }) {
  return (
    <div className="rounded-md p-1.5 md:p-2"
      style={{
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8), 0 0 0 2px #333',
      }}>
      {label && (
        <div className="text-center text-[10px] md:text-xs uppercase tracking-[0.25em] mb-1" style={{ color: '#7a7a7a' }}>
          {label}
        </div>
      )}
      <div
        className={`text-center ${big ? 'text-3xl md:text-6xl' : 'text-2xl md:text-4xl'} font-bold tabular-nums`}
        style={{
          fontFamily: ledFont,
          color: '#ff3a1c',
          textShadow: '0 0 6px rgba(255,58,28,0.85), 0 0 14px rgba(255,58,28,0.55), 0 0 24px rgba(255,58,28,0.35)',
          letterSpacing: '0.12em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

// =================== FACTORY ===================
export function renderTemplate({ client, theme, fields, items }) {
  const id = theme.template_id || 'grid';
  const props = { client, theme, fields, items };
  switch (id) {
    case 'promo_banner': return <PromoBannerTemplate {...props} />;
    case 'menu_list': return <MenuListTemplate {...props} />;
    case 'minimalist_card': return <MinimalistCardTemplate {...props} />;
    case 'split_screen': return <SplitScreenTemplate {...props} />;
    case 'weather_widget': return <WeatherWidgetTemplate {...props} />;
    case 'gold_rate_board': return <GoldRateBoardTemplate {...props} />;
    case 'grid':
    default:
      return <GridTemplate {...props} />;
  }
}

export const TEMPLATE_IDS = ['grid', 'promo_banner', 'menu_list', 'minimalist_card', 'split_screen', 'weather_widget', 'gold_rate_board'];
